import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { BsArrowRight } from "react-icons/bs";
import { IoArrowBackOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import CartContext from "../context/CartContext";
import { postOrder } from "../redux/features/orderSlice";
import {
  createAddress,
  getAddress,
} from "../redux/features/shippingAddressSlice";

const Checkout = () => {
  const { cartSummary, setCartSummary } = useContext(CartContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const me = useSelector((state) => state.login.me);
  const cartData = useSelector((state) => state.cart.carts);
  const isLoading = useSelector((state) => state.order.isLoading);
  const setting = useSelector((state) => state.setting.settings);
  const address = useSelector((state) => state.shipping.address);

  const [isSavedAddress, setIsSavedAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Calculate total quantity and price
  const totalQuantity = cartData?.reduce(
    (total, cart) => total + (cart.quantity || 0),
    0
  );
  const totalPrice = cartData?.reduce((total, item) => {
    const price = item?.product
      ? Number(item.product.selling_price)
      : item?.variation?.selling_price;

    const quantity = item.quantity ? Number(item.quantity) : 1;
    return total + price * quantity;
  }, 0);

  const deliveryCharge = setting.delivery_charges || 0;
  const grandTotal = totalPrice + deliveryCharge;

  // Validation schema for the address form
  const validationSchema = Yup.object({
    name: Yup.string().required("Required"),
    address: Yup.string().required("Required"),
    city: Yup.string().required("Required"),
    state: Yup.string().required("Required"),
    zip_code: Yup.string()
      .required("Required")
      .matches(/^\d{5}(-\d{4})?$/, "Invalid ZIP code"),
    landmark: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
  });

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      landmark: "",
      country: "",
    },
    ...(!selectedAddress && { validationSchema: validationSchema }),
    onSubmit: async (values) => {
      try {
        let shippingAddressId = null;

        // If a saved address is selected, use its ID
        if (selectedAddress) {
          shippingAddressId = selectedAddress;
        } else if (!isSavedAddress) {
          // If a new address is being created, send it to the server
          const addressData = {
            name: values.name,
            address: values.address,
            city: values.city,
            state: values.state,
            zip_code: values.zip_code,
            landmark: values.landmark,
            country: values.country,
          };

          // Dispatch the action to create a new address
          const response = await dispatch(createAddress(addressData)).unwrap();
          shippingAddressId = response.data.id; // Assuming the response contains the new address ID
        } else {
          // If neither a saved address nor a new address is provided, throw an error
          throw new Error("No address mentioned or created.");
        }

        // Prepare order items
        const orderItems = cartData?.map((item) => ({
          ...(item?.product?.id && { product: item?.product?.id }),
          ...(item?.variation?.id && {
            variation: item?.variation?.id,
          }),
          quantity: item?.quantity,
          price: item?.product?.price || item?.variation?.price,
        }));

        // Prepare order data
        const orderData = {
          shipping_address: shippingAddressId || selectedAddress,
          order_items: orderItems,
          note: "",
        };

        // Dispatch the action to place the order
        dispatch(postOrder(orderData)).then(() => {
          // navigate("/confirm-order");
        });
      } catch (error) {
        console.error("Error during order submission:", error.message);
        alert(error.message); // Show error to the user
      }
    },
  });

  // Fetch saved addresses on component mount
  useEffect(() => {
    dispatch(getAddress());
  }, [dispatch]);

  // Update form values when a saved address is selected
  useEffect(() => {
    if (selectedAddress) {
      const addr = me?.addresses?.find((addr) => addr?.id === selectedAddress);
      formik.setValues({
        name: addr?.name || "",
        address: addr?.address || "",
        city: addr?.city || "",
        state: addr?.state || "",
        zip_code: addr?.zip_code || "",
        landmark: addr?.landmark || "",
        country: addr?.country || "",
      });
    } else {
      formik.resetForm();
    }
  }, [selectedAddress]);

  return (
    <div className="container px-4 py-14 lg:px-0">
      {cartData?.length === 0 ? (
        <div>
          <p className="mb-5 text-2xl font-medium">Your cart is empty.</p>
          <Link to="/">
            <button className="flex items-center gap-1 p-3 px-5 text-white uppercase duration-300 bg-black border border-black hover:bg-white hover:text-black">
              <IoArrowBackOutline /> Go back to shopping
            </button>
          </Link>
        </div>
      ) : (
        <>
          <h2 className="pb-20 text-3xl font-bold text-center uppercase">
            Checkout
          </h2>
          <form onSubmit={formik.handleSubmit}>
            <div className="flex flex-col gap-24 md:flex-row">
              <div className="md:w-[60%]">
                <h2 className="pb-5 text-lg font-bold uppercase">Contact</h2>
                <div className="flex items-end gap-5">
                  <TextField
                    type="text"
                    label="Enter Phone"
                    className="p-2 border border-gray-400 w-72"
                    name="phone"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                  />
                </div>
                <hr className="w-full my-5 border border-gray-300 mt-7" />
                <div className="flex flex-col gap-4">
                  <h2 className="text-lg font-bold uppercase">Address</h2>
                  <div
                    onClick={() => {
                      setIsSavedAddress((prev) => !prev);
                      setSelectedAddress(null);
                    }}
                    className={`${
                      isSavedAddress ? "bg-black text-white" : "bg-white"
                    } border rounded-lg px-4 py-1 w-fit cursor-pointer`}
                  >
                    {isSavedAddress ? "Enter New Address" : "Use Saved Address"}
                  </div>

                  {isSavedAddress ? (
                    <div className="flex flex-wrap gap-5 rounded-lg">
                      {address?.map((addr, index) => (
                        <div
                          className={`${
                            selectedAddress === addr?.id
                              ? "bg-black text-white"
                              : "bg-gray-100"
                          } rounded-lg p-4 cursor-pointer`}
                          key={index}
                          onClick={() => setSelectedAddress(addr?.id)}
                        >
                          <div className="flex items-center gap-2">
                            <label>Name: </label>
                            <span>{addr?.name} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>Address: </label>
                            <span>{addr?.address} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>City: </label>
                            <span>{addr?.city} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>State: </label>
                            <span>{addr?.state} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>ZIP Code: </label>
                            <span>{addr?.zip_code} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>Landmark: </label>
                            <span>{addr?.landmark} </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label>Country: </label>
                            <span>{addr?.country} </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className="pb-4 font-semibold">Delivery address</p>
                      <div className="grid grid-cols-2 gap-5">
                        <TextField
                          type="text"
                          label="Name"
                          className="col-span-2"
                          name="name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.name && Boolean(formik.errors.name)
                          }
                          helperText={formik.touched.name && formik.errors.name}
                        />
                        <TextField
                          type="text"
                          label="Address"
                          className="col-span-2"
                          name="address"
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.address &&
                            Boolean(formik.errors.address)
                          }
                          helperText={
                            formik.touched.address && formik.errors.address
                          }
                        />
                        <TextField
                          type="text"
                          label="City"
                          name="city"
                          value={formik.values.city}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.city && Boolean(formik.errors.city)
                          }
                          helperText={formik.touched.city && formik.errors.city}
                        />
                        <TextField
                          type="text"
                          label="State"
                          name="state"
                          value={formik.values.state}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.state && Boolean(formik.errors.state)
                          }
                          helperText={
                            formik.touched.state && formik.errors.state
                          }
                        />
                        <TextField
                          type="text"
                          label="ZIP Code"
                          name="zip_code"
                          value={formik.values.zip_code}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.zip_code &&
                            Boolean(formik.errors.zip_code)
                          }
                          helperText={
                            formik.touched.zip_code && formik.errors.zip_code
                          }
                        />
                        <TextField
                          type="text"
                          label="Landmark"
                          name="landmark"
                          value={formik.values.landmark}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.landmark &&
                            Boolean(formik.errors.landmark)
                          }
                          helperText={
                            formik.touched.landmark && formik.errors.landmark
                          }
                        />
                        <TextField
                          type="text"
                          label="Country"
                          name="country"
                          value={formik.values.country}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.country &&
                            Boolean(formik.errors.country)
                          }
                          helperText={
                            formik.touched.country && formik.errors.country
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="md:w-[40%]">
                <div className="bg-[#F5F5F5] rounded-md sticky top-36 px-7 py-8">
                  <h2 className="pb-3 text-lg font-bold uppercase">
                    Order Summary
                  </h2>
                  <div className="flex justify-between pb-3">
                    <h2>Total Items:</h2>
                    <h2>{totalQuantity}</h2>
                  </div>
                  <div className="flex justify-between pb-3">
                    <h2>Total Price: </h2>
                    <h2>Rs. {totalPrice}</h2>
                  </div>
                  <div className="flex justify-between pb-3">
                    <h2>Delivery Charge: </h2>
                    <h2>Rs. {deliveryCharge}</h2>
                  </div>
                  <hr className="border-gray-400" />
                  <div className="flex justify-between pt-3">
                    <h2>Grand Total:</h2>
                    <h2>Rs. {grandTotal}</h2>
                  </div>
                  <button
                    disabled={isLoading}
                    type="submit"
                    className="bg-black text-white w-full p-4 mt-7 uppercase text-[15px] font-medium flex items-center justify-center gap-2 rounded-md hover:bg-[#242424] duration-300"
                  >
                    {isLoading ? "Loading ..." : "Place Order"}
                    <span>
                      <BsArrowRight className="text-lg" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default Checkout;
